import {
  GET_LIST_REQUEST_DEFAULT_LIMIT,
  PAGE_SEARCH_PARAMETER
} from "constants/common";
import { useManageTable } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  GetResellerCommissionsRequestParams,
  useGetResellerCommissions
} from "../apis";
import {
  COMMISSION_FROM_FILTER_SEARCH_PARAMETER,
  COMMISSION_SOURCE_FILTER_SEARCH_PARAMETER,
  COMMISSION_STATUS_FILTER_SEARCH_PARAMETER,
  COMMISSION_TO_FILTER_SEARCH_PARAMETER,
  ResellerCommissionSource,
  ResellerCommissionsOrderBy,
  ResellerCommissionStatus
} from "../constants";

/**
 * The KPI tiles come back with the page: the backend computes them over the
 * whole eligible set whatever chip is active, so switching the source chip
 * never makes them jump.
 */
const useResellerCommissionsList = () => {
  const { resellerId } = useParams();

  const {
    page,
    searchValue,
    debouncedSearch,
    sorting,
    setSorting,
    setSearchValue,
    setPage
  } = useManageTable({
    defaultSorting: [{ id: ResellerCommissionsOrderBy.Date, desc: true }]
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const source = searchParams.get(
    COMMISSION_SOURCE_FILTER_SEARCH_PARAMETER
  ) as ResellerCommissionSource | null;
  const status = searchParams.get(
    COMMISSION_STATUS_FILTER_SEARCH_PARAMETER
  ) as ResellerCommissionStatus | null;
  const from = searchParams.get(COMMISSION_FROM_FILTER_SEARCH_PARAMETER);
  const to = searchParams.get(COMMISSION_TO_FILTER_SEARCH_PARAMETER);

  const setSearchParameter = (key: string, value: string | null) => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    // Any filter change invalidates the current page number.
    searchParams.delete(PAGE_SEARCH_PARAMETER);
    setSearchParams(searchParams);
  };

  const setSource = (nextSource: ResellerCommissionSource | null) => {
    setSearchParameter(COMMISSION_SOURCE_FILTER_SEARCH_PARAMETER, nextSource);
  };

  const requestParams: GetResellerCommissionsRequestParams = useMemo(
    () => ({
      page,
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch || undefined,
      orderBy:
        (sorting[0]?.id as ResellerCommissionsOrderBy) ??
        ResellerCommissionsOrderBy.Date,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "DESC",
      source: source ?? undefined,
      status: status ?? undefined,
      from: from ?? undefined,
      to: to ?? undefined
    }),
    [page, debouncedSearch, sorting, source, status, from, to]
  );

  const {
    commissions,
    meta,
    summary,
    shownTotal,
    isLoading,
    isFetching,
    refetch
  } = useGetResellerCommissions(String(resellerId), requestParams);

  return {
    resellerId: String(resellerId),
    commissions,
    summary,
    shownTotal,
    totalItems: meta?.itemCount ?? 0,
    isLoading: isLoading || isFetching,
    page,
    setPage,
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    source,
    setSource,
    status,
    from,
    to,
    requestParams,
    refetch
  };
};

export default useResellerCommissionsList;
